// client/src/components/DataInquiryForm.tsx
import React, { useState } from "react";
import "./DataInquiryForm.css";
import { Field, FilePicker } from "../components/FormControls";
import { filterAllowedAndSize } from "../../utils/files";
import { Form, Errors, fieldError } from "../../utils/validationUtils";
import { dataInquiryJson } from "../../api/api";
import { uploadFiles } from "../../api/uploadFiles";
import SuccessPopup from "../components/SuccessPopup";
import arrowLeft from "../../assets/arrow-left.png";
import superpharmLogo from "../../assets/superpharm-logo.svg";

const initial: Form = { id: "", fullname: "", email: "", phone: "", details: "", agree: false, liability: false, privacy: false, read: false };

export default function DataInquiryForm() {
  const [form, setForm] = useState<Form>(initial);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const t = e.target as HTMLInputElement;
    const { name, type, value } = t;
    const nextVal = type === "checkbox" ? t.checked : value;
    setForm(f => ({ ...f, [name]: nextVal as any }));
    setErrors(er => ({ ...er, [name]: fieldError(name, value) }));
  };

  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files).slice(0, 3) : [];
    const { ok } = filterAllowedAndSize(picked);
    setFiles(ok);
    setErrors(er => ({ ...er, files: ok.length <= 3 ? undefined : "עד 3 קבצים" }));
  };

  const validateForm = () => {
    const newErrors: Errors = {
      fullname: fieldError('fullname', form.fullname),
      id: fieldError('id', form.id),
      phone: fieldError('phone', form.phone),
      email: fieldError('email', form.email),
      details: !form.details.trim() ? "נא לפרט את הבקשה" : undefined,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(err => err);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const attachments = files.length > 0 ? await uploadFiles(files) : [];
      const response = await dataInquiryJson({ ...form, attachments });

      if (response?.success) {
        setShowSuccess(true);
        setForm(initial);
        setFiles([]);
        setErrors({});
      } else {
        alert(response?.message || 'שליחת הטופס נכשלה. נסה שוב מאוחר יותר.');
      }
    } catch (err: any) {
      alert(err?.message || 'שליחת הטופס נכשלה. נסה שוב מאוחר יותר.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main dir="rtl" className="page">
      <SuccessPopup open={showSuccess} onClose={() => setShowSuccess(false)} />
      <div className="container">
        <div className="card">
          <div className="di-header">
            <img src={superpharmLogo} alt="Super-Pharm" className="di-logo" />
          </div>
          <div className="di-title-wrap">
            <h1 className="di-title">טופס הגשת בקשת עיון – נושא מידע</h1>
            <p className="di-subtitle">טופס בקשה למימוש זכויות עיון במידע אישי</p>
          </div>

          <div className="intro-wrap">
            <p className="intro-to">אל: סופר-פארם ("החברה")</p>
            <p className="intro-text">
              בהתאם להוראות חוק הגנת הפרטיות, תשמ"א-1981 ותקנות הגנת הפרטיות (תנאים לעיון במידע וסדרי הדין בערעור על סירוב לבקשת עיון), תשמ"א- 1981 אבקשכם לאפשר לי לעיין במידע אודותיי, המצוי במאגר המידע שברשותכם, כמפורט להלן.
            </p>
          </div>

          <form className="di-form" onSubmit={handleSubmit} onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target instanceof HTMLElement && e.target.tagName !== 'BUTTON' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}>
            <p className="form-header">פרטי המבקש/ת:</p>

            <div className="di-form-fields">
              <div className="di-grid">
                <div className="di-field-fullname">
                  <Field id="fullname" name="fullname" label="שם מלא" value={form.fullname} onChange={onChange} error={errors.fullname} classPrefix="di-" />
                </div>
                <div className="di-field-id">
                  <Field id="id" name="id" inputMode="numeric" label="מספר ת.ז/דרכון" value={form.id} onChange={onChange} error={errors.id} classPrefix="di-" />
                </div>
                <div className="di-field-phone">
                  <Field id="phone" name="phone" inputMode="tel" label="טלפון" value={form.phone} onChange={onChange} error={errors.phone} classPrefix="di-" />
                </div>
                <div className="di-field-email">
                  <Field id="email" name="email" type="email" label="אימייל" value={form.email} onChange={onChange} error={errors.email} classPrefix="di-" />
                </div>
              </div>
            </div>

            <div className="info-text">
              <p className="info-text-p">באם הטופס מוגש באמצעות בא כח המבקש. יש לצרף ייפוי כוח מתאים.</p>
              <p className="info-text-p">באם הטופס מוגש באמצעות אפוטרופוס המבקש. יש לצרף מסמכי אישור בהתאם לדין.</p>
            </div>

            <div className="other-files">
              <FilePicker label="צירוף קבצים" files={files} onChange={onFiles} multiple error={errors.files}
                title="אפשר להעלות עד 3 קבצים מסוג תמונה או PDF." />
            </div>

            <div className="di-field">
              <div className="textarea-float-wrap-details">
                <div className="form-header">אנא פרט/י מהו המידע המבוקש:</div>
                <Field
                  id="details"
                  name="details"
                  textarea
                  className={`${errors.details ? "input-error" : ""}`}
                  label="פירוט הבקשה"
                  maxLength={3000}
                  value={form.details}
                  onChange={onChange}
                  classPrefix="di-"
                />
                {errors.details && <div className="error-text">{errors.details}</div>}
                <div className="info-text-company">
                  החברה תבחן את בקשת העיון, וככל שהנך זכאי/ת לקבל את המידע על פי הדין, העתק המידע המבוקש (או קישור) יועבר אליך לכתובת הדוא"ל שצויינה לעיל בתוך 30 יום ממועד קבלת הבקשה וביצוע אימות זהותך. המידע שיישלח אליך כולל רק מידע שניתן למסרו על פי נהלי החברה והניתן לאיתור באופן סביר.                </div>
              </div>
            </div>

            <p className="form-header">כללי:</p>
            <p className="info-text-company">בשליחת הטופס אני מאשר כי:</p>
            <div className="consent-texts">
              <p className="consent-text-item">
                אני מצהיר/ה כי הפרטים המבוקשים על ידי נוגעים אליי, וכי כתובת הדוא"ל שפירטתי לעיל הינה כתובתי האישית אשר אליה אני מבקש כי החברה תעביר מידע אישי אודותיי כמפורט בטופס זה.
              </p>
              <p className="consent-text-item">
                אני פוטר/ת בזאת את החברה מכל אחריות לנזק כתוצאה מביצוע בקשתי לעיל, לרבות במקרה בו עקב בקשתי זו יעבור מידע כלשהו לצדדים שלישיים.
              </p>
              <p className="consent-text-item">
                אני מודע/ת לכך שלא קיימת חובה חוקית למסור את המידע והנתונים, אולם מסירתם נדרשת לשם הטיפול בבקשתי באופן תקין ויעיל ומסירתה נעשית מרצוני החופשי. הנני מסכים/ה כי פרטי הפנייה ותוצאותיה יישמרו בידי החברה לצורך הגנה על זכויותיה, ובהתאם <a href="https://shop.super-pharm.co.il/privacy_policy" target="_blank" rel="noopener noreferrer" style={{ color: '#263E98', textDecoration: 'underline' }}>למדיניות הפרטיות</a> של החברה.
              </p>
              <p className="consent-text-item">
                פעולות של מיופה כוח לפי טופס זה מחייבות את נושא המידע עצמו, ונושא המידע פוטר את החברה מכל אחריות בקשר עם ביצוע פעולות ו/או מסירת מידע לידי מיופה הכוח.
              </p>
            </div>
            <div className="bullets">
              <p>לפניות נוספות ניתן לפנות לכתובת - <a href="mailto:contactus@super-pharm.co.il" style={{ color: '#263E98', textDecoration: 'underline' }}>contactus@super-pharm.co.il</a></p>
            </div>

            <div className="di-submit">
              <button type="submit" className="btn" disabled={loading}>{loading ? "שולח..." : "אישור טופס הבקשה ושליחתה"}</button>
            </div>
          </form>
        </div>
      </div >
    </main >
  );
}
